import doWhen from "@gef-ui/utils/doWhen";
import { VALID } from '@gef-ui/validations/constants';
import PropTypes from 'prop-types';
import assoc from "ramda/es/assoc";
import both from "ramda/es/both";
import compose from "ramda/es/compose";
import flip from "ramda/es/flip";
import identity from "ramda/es/identity";
import includes from "ramda/es/includes";
import is from "ramda/es/is";
import isNil from "ramda/es/isNil";
import keys from "ramda/es/keys";
import o from "ramda/es/o";
import prop from "ramda/es/prop";
import when from "ramda/es/when";
import isNotNil from "ramda-extension/es/isNotNil";
import notEqual from "ramda-extension/es/notEqual";
import { Component, createElement } from 'react';
import { fieldTypes } from '../../../constants';
import componentCreator from '../componentCreator';
import fieldConnector from '../fieldConnector';
const isString = is(String);
const hasErrors = compose(notEqual(VALID), prop('valid'));
const rapidComponents = [
    fieldTypes.TEXT_FIELD,
    fieldTypes.SPINNER,
    fieldTypes.AUTOCOMPLETE,
    fieldTypes.AUTOCOMPLETE_CODELIST
];
const isRapidComponent = o(flip(includes)(rapidComponents), prop('component'));
const hasRapidMode = both(hasErrors, isRapidComponent);
const hasDefinedViewParser = both(notEqual(identity), isNotNil);
const changeFocus = (focus)=>(prevState)=>assoc('focus', focus, prevState);
const changeRapid = (rapidValidationMode)=>(prevState)=>assoc('rapidValidationMode', rapidValidationMode, prevState);
class Field extends Component {
    constructor(props){
        super(props);
        this.state = {
            rapidValidationMode: false,
            focus: false
        };
        const { register, initiallyDirty, fieldName, validate, section, disabled, component, addFileInput, temp, errMessage, updateFieldState } = props;
        register({
            fieldName,
            section,
            initiallyDirty: initiallyDirty || !!errMessage,
            temp
        });
        updateFieldState({
            [fieldName]: {
                disabled: disabled || false
            }
        });
        this.defaultStoreValue();
        doWhen(!disabled, ()=>validate([
                fieldName
            ]));
        doWhen(component === fieldTypes.FILE_INPUT, ()=>addFileInput(fieldName));
    }
    static getDerivedStateFromProps(props, state) {
        if (hasRapidMode(props) && !state.rapidValidationMode) {
            return changeRapid(true)(state);
        } else {
            return null;
        }
    }
    componentDidUpdate(prevProps) {
        const { value, disabled, updateFieldState, fieldName } = this.props;
        if (isNil(value)) {
            this.defaultStoreValue();
        }
        if (prevProps.disabled !== disabled) {
            updateFieldState({
                [fieldName]: {
                    disabled
                }
            });
        }
    }
    componentWillUnmount() {
        const { unregister, component, fieldName, removeFileInput, errMessage, clearGlobalErrors } = this.props;
        unregister(fieldName);
        if (errMessage) {
            clearGlobalErrors([
                {
                    fieldName
                }
            ]);
        }
        doWhen(component === fieldTypes.FILE_INPUT, ()=>removeFileInput(fieldName));
    }
    defaultStoreValue = ()=>{
        const { defaultFieldValue, defaultValue, deepPath, temp } = this.props;
        if (isNotNil(defaultValue)) {
            // action decides if it should update
            defaultFieldValue(deepPath, defaultValue, temp);
        }
    };
    handleChange = (_e, value)=>{
        const { updateFieldValue, validate, deepPath, fieldName, pristine, updateFieldState, updateFormState, temp } = this.props;
        updateFormState({
            pristine: false
        });
        updateFieldValue(deepPath, value, temp);
        doWhen(pristine, ()=>updateFieldState({
                [fieldName]: {
                    pristine: false
                }
            }));
        doWhen(this.state.rapidValidationMode, ()=>validate([
                fieldName
            ]));
    };
    handleUpdate = (e, value)=>{
        this.handleChange(e, this.props.modelParser(value ?? e.target.value, e));
    };
    handleValidate = ()=>{
        this.props.validate([
            this.props.fieldName
        ]);
        if (this.state.rapidValidationMode && !hasRapidMode(this.props)) {
            this.setState(changeRapid(false));
        }
    };
    handleAsyncValidate = ()=>{
        this.props.validateAsync([
            this.props.fieldName
        ]);
    };
    handleFocusTrue = ()=>this.setState(changeFocus(true));
    handleFocusFalse = ()=>this.setState(changeFocus(false));
    handleTabKeyFocus = (event)=>{
        const { viewParser } = this.props;
        if (event.key === 'Tab' && hasDefinedViewParser(viewParser)) {
            event.target.select();
        }
    };
    render() {
        const { updateFieldValue, defaultFieldValue, validate, register, unregister, pristine, initiallyDirty, updateFieldState, deepPath, section, formName, updateFormState, temp, modelParser, addFileInput, removeFileInput, validateAsync, clearGlobalErrors, defaultValue, component, value, viewParser, ...other } = this.props;
        const componentFn = when(isString, componentCreator)(component);
        const defaultedValue = isNotNil(value) ? value : defaultValue;
        return /*#__PURE__*/ createElement(componentFn, {
            handleUpdate: this.handleUpdate,
            handleChange: this.handleChange,
            handleValidate: this.handleValidate,
            handleAsyncValidate: this.handleAsyncValidate,
            handleFocusTrue: this.handleFocusTrue,
            handleFocusFalse: this.handleFocusFalse,
            handleKeyUp: this.handleTabKeyFocus,
            value: defaultedValue,
            displayValue: this.state.focus ? defaultedValue : viewParser(defaultedValue),
            viewParser,
            focus: this.state.focus,
            formName,
            ...other
        });
    }
}
Field.defaultProps = {
    viewParser: identity,
    modelParser: identity,
    initiallyDirty: false,
    temp: false
};
Field.propTypes = {
    /**
	 * adds fileInput field deep path to form state. [mapped by redux]
	 */ addFileInput: PropTypes.func,
    /**
	 * True if async validation is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    /**
	 * Clear global errors function
	 */ clearGlobalErrors: PropTypes.func,
    /**
	 * Value is supplied to codelist select. [supplied from ProductField]
	 */ codelistName: PropTypes.string,
    /**
	 * Type of component, can be one of fieldTypes or custom function
	 */ component: PropTypes.oneOfType([
        PropTypes.oneOf(keys(fieldTypes)),
        PropTypes.func
    ]).isRequired,
    /**
	 * CreateOptions function is used for creating options for select or radio button.
	 * Both of them has default implementation.
	 */ createOptions: PropTypes.func,
    /**
	 * deepPath to the formData/temporaryData. [taken from descriptor]
	 */ deepPath: PropTypes.string,
    /**
	 * Default store value action. [mapped by redux]
	 */ defaultFieldValue: PropTypes.func,
    /**
	 * Default value of the field. [supplied from ProductField]
	 */ defaultValue: PropTypes.any,
    /**
	 * disabled/enabled
	 */ disabled: PropTypes.bool,
    /**
	 * error message shown in field
	 */ errMessage: PropTypes.string,
    /**
	 * fieldName, must be same as in descriptor.
	 */ fieldName: PropTypes.string.isRequired,
    /**
	 * name of the form where the field belongs e.g. NegotiationForm. [supplied from withForm]
	 */ formName: PropTypes.string,
    /**
	 * Field is dirty at registration
	 */ initiallyDirty: PropTypes.bool,
    /**
	 * label for field. [taken from descriptor]
	 */ label: PropTypes.string,
    /**
	 * Function to modify model value
	 *
	 * @param {Any} value to modify.
	 * @param {Object} triggered event.
	 * @return {Any} modified value
	 */ modelParser: PropTypes.func,
    /**
	 * custom onChange function.
	 */ onChange: PropTypes.func,
    /**
	 * options for radio button/select
	 */ options: PropTypes.array,
    /**
	 * options filter for select
	 */ optionsFilter: PropTypes.func,
    /**
	 * pristine. [taken from state]
	 */ pristine: PropTypes.bool,
    /**
	 * register action. [mapped by redux]
	 */ register: PropTypes.func,
    /**
	 * removes field deep path from form state. [mapped by redux]
	 */ removeFileInput: PropTypes.func,
    /**
	 * Section of field in the form e.g. AgentSection. [supplied from withSection]
	 */ section: PropTypes.string,
    /**
	 * If temporary, field value is stored into temporary data
	 */ temp: PropTypes.bool,
    /**
	 * unregisted action. [mapped by redux]
	 */ unregister: PropTypes.func,
    /**
	 * update field state. [mapped by redux]
	 */ updateFieldState: PropTypes.func,
    /**
	 * update store field value action. [mapped by redux]
	 */ updateFieldValue: PropTypes.func,
    /**
	 * update form state. [mapped by redux]
	 */ updateFormState: PropTypes.func,
    /**
	 * valid validation. [taken from state]
	 */ valid: PropTypes.string,
    /**
	 * validate action. [mapped by redux]
	 */ validate: PropTypes.func,
    /**
	 * async validate action. [mapped by redux]
	 */ validateAsync: PropTypes.func,
    /**
	 * value of the field. [taken from state]
	 */ value: PropTypes.any,
    /**
	 * Function for modify view value
	 */ viewParser: PropTypes.func
};
export { Field };
export default fieldConnector(Field);
